import React, { useRef, useEffect, useMemo, useState } from "react";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { monedaSimbolo } from "../../utils/formatters";

Chart.register(...registerables, ChartDataLabels);

export default function CardBarChart({ data = [], currency = "USD", isLoading, title = "Ventas por Periodo" }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [filter, setFilter] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Etiquetas: se ajustan a la moneda indicada por el filtro de datos.
  // `currency` lo provee el backend (getSalesOverTime) segun el filtro
  // seleccionado en el Dashboard (VES / USD / EUR / extranjera).
  const simbolo = monedaSimbolo(currency);
  const yAxisLabel = currency ? `Monto (${simbolo})` : "Monto";
  const label = currency ? `Monto Neto (${simbolo})` : "Monto Neto";

  // Labels y valores
  const labels = useMemo(() => {
    return data.map((item) => {
      const [year, month] = item.periodo.split("-");
      return new Date(year, month - 1).toLocaleDateString("es-ES", {
        month: "long",
        year: "numeric",
      });
    });
  }, [data]);

  const values = useMemo(() => data.map((item) => Number(item.neto_total)), [data]);

  const { filteredLabels, filteredValues } = useMemo(() => {
    if (!filter) return { filteredLabels: labels, filteredValues: values };
    const fL = [];
    const fV = [];
    labels.forEach((l, i) => {
      if (l.toLowerCase().includes(filter.toLowerCase())) {
        fL.push(l);
        fV.push(values[i]);
      }
    });
    return { filteredLabels: fL, filteredValues: fV };
  }, [labels, values, filter]);

  // Formatter para datalabels
  const dataLabelFormatter = (value) =>
    `${value.toLocaleString("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Crear y actualizar chart
  useEffect(() => {
    if (!canvasRef.current) return;

    // Gradiente Sirius para las barras (#4551F7 -> #3039AC)
    const ctx = canvasRef.current.getContext("2d");
    const barGradient = ctx.createLinearGradient(0, 0, 0, 320);
    barGradient.addColorStop(0, "#4551F7");
    barGradient.addColorStop(1, "#3039AC");

    if (!chartRef.current) {
      chartRef.current = new Chart(canvasRef.current, {
        type: "bar",
        data: {
          labels: filteredLabels,
          datasets: [
            {
              label: label,
              data: filteredValues,
              backgroundColor: barGradient,
              hoverBackgroundColor: "#4551F7",
              borderRadius: 8,
              maxBarThickness: 26,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: "top",
              labels: { font: { family: "Kanit", size: 12, weight: "600" }, color: "#3039AC", usePointStyle: true, pointStyle: "rectRounded" },
            },
            tooltip: {
              backgroundColor: "rgba(13,13,16,0.92)",
              titleFont: { family: "Kanit", weight: "600" },
              bodyFont: { family: "Kanit" },
              padding: 12,
              cornerRadius: 10,
              displayColors: false,
              callbacks: {
                label: (ctx) => {
                  const value = ctx.parsed.y;
                  return `${value.toLocaleString("es-ES", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`;
                },
              },
            },
            datalabels: {
              anchor: "end",
              align: "end",
              formatter: dataLabelFormatter,
              font: { family: "Kanit", weight: "bold", size: 10 },
              color: "#3039AC",
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { family: "Kanit" }, color: "#5a5a64" },
              title: { display: true, text: "Mes / Periodo", font: { family: "Kanit", size: 13, weight: "bold" }, color: "#3039AC" }
            },
            y: {
              beginAtZero: true,
              grid: { color: "rgba(48,57,172,0.08)" },
              ticks: { font: { family: "Kanit" }, color: "#5a5a64" },
              title: { display: true, text: yAxisLabel, font: { family: "Kanit", size: 13, weight: "bold" }, color: "#3039AC" }
            },
          },
        },
      });
    } else {
      chartRef.current.data.labels = filteredLabels;
      chartRef.current.data.datasets[0].data = filteredValues;
      chartRef.current.data.datasets[0].label = label;
      chartRef.current.data.datasets[0].backgroundColor = barGradient;

      // Actualiza título eje Y y formatter datalabels
      chartRef.current.options.scales.y.title.text = yAxisLabel;
      chartRef.current.options.plugins.datalabels.formatter = dataLabelFormatter;

      chartRef.current.update();
    }
  }, [filteredLabels, filteredValues, yAxisLabel, label, currency]);

  // Descargar chart
  const downloadChart = (format = "png") => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    let mimeType = "image/png";
    if (format === "jpg") mimeType = "image/jpeg";

    const url = canvas.toDataURL(mimeType, 1);
    const link = document.createElement("a");
    link.href = url;
    link.download = `grafico.${format}`;
    link.click();
    setDropdownOpen(false);
  };

  return (
    <div className="sirius-card relative flex flex-col w-full">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white bg-gradient-to-br from-sirius-600 to-spark-500 shadow-spark">
            <i className="fas fa-chart-column" />
          </span>
          <div>
            <h6 className="uppercase text-ink-400 text-[0.66rem] font-display font-semibold tracking-[0.14em]">Análisis</h6>
            <h2 className="text-ink-900 text-xl font-display font-bold leading-tight">{title}</h2>
          </div>
        </div>

        {/* Filtro + Dropdown de descarga */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Filtrar por mes…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="hidden sm:block border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-ink-700 focus:outline-none focus:ring-2 focus:ring-sirius-400/30"
          />
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center justify-center w-9 h-9 rounded-lg text-white bg-gradient-to-br from-sirius-600 to-spark-500 shadow-spark hover:-translate-y-0.5 transition-all"
              data-tooltip="Descargar gráfico (PNG / JPG)"
              data-tooltip-pos="left"
              aria-label="Descargar gráfico"
            >
              <i className="fas fa-download text-sm" />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-28 bg-white border border-slate-200 rounded-xl shadow-sirius z-20 overflow-hidden">
                <button
                  onClick={() => downloadChart("png")}
                  className="w-full text-left px-4 py-2 hover:bg-sirius-50 hover:text-sirius-700 text-sm font-medium transition-colors"
                >
                  PNG
                </button>
                <button
                  onClick={() => downloadChart("jpg")}
                  className="w-full text-left px-4 py-2 hover:bg-sirius-50 hover:text-sirius-700 text-sm font-medium transition-colors"
                >
                  JPG
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filtro (mobile) */}
      <div className="sm:hidden px-5 pt-3">
        <input
          type="text"
          placeholder="Filtrar por mes…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-ink-700 focus:outline-none focus:ring-2 focus:ring-sirius-400/30"
        />
      </div>

      {/* Chart */}
      <div className="p-5 relative h-72 md:h-80">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm z-10 rounded-xl">
            <i className="fas fa-circle-notch fa-spin text-sirius-600 text-3xl"></i>
          </div>
        )}
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}
